// The defence orders on a unit's card (module `espionage.counter_intel`):
// guard a city, stand down, sweep for hidden agents, try to catch a detected
// one.
//
// The sim lists what the unit may do (`counter`): the cities beside it, the
// agents it could catch, whether it can sweep. A refusal comes back as `false`.

import { useState } from 'react';
import type { CounterRules, Spy } from '../../espionage/espionage.js';
import { useT } from '../../i18n/index.js';
import { appDom } from '../../lib/appDom.js';
import type { OpsActions } from '../../sim/useSimWorld/useEspionageIntents.js';

export interface CounterControlsProps {
  readonly rules: CounterRules;
  readonly spy: Spy;
  readonly actions: OpsActions;
}

const BUTTON =
  'rounded border border-border/60 px-2 py-0.5 text-left text-[10px] hover:bg-muted/60';

export function CounterControls({ rules, spy, actions }: CounterControlsProps) {
  const t = useT();
  const [refused, setRefused] = useState(false);
  const counter = spy.counter;
  if (!counter) return null;
  const nothing =
    !counter.post &&
    counter.postable.length === 0 &&
    counter.interceptable.length === 0 &&
    !counter.canSweep;
  if (nothing) return null;
  const run = (ok: boolean) => setRefused(!ok);
  return (
    <div className="flex flex-col gap-1" data-testid={`spy-counter-${spy.unitId}`}>
      {counter.post ? (
        <div className="flex items-center gap-2 text-[10px]">
          <span data-testid={`spy-counter-post-${spy.unitId}`}>
            {t('espionage.counterGuarding', { city: counter.post.name })}
          </span>
          <button
            type="button"
            data-testid={`spy-counter-recall-${spy.unitId}`}
            {...appDom({
              id: `spy-counter-recall-${spy.unitId}`,
              type: 'Button',
              role: 'button',
              scope: 'section',
            })}
            onClick={() => run(actions.recallCounterSpy(spy.unitId))}
            className={`ml-auto ${BUTTON}`}
          >
            {t('espionage.counterRecall')}
          </button>
        </div>
      ) : (
        counter.postable.map((city) => (
          <button
            key={city.cityId}
            type="button"
            data-testid={`spy-counter-guard-${spy.unitId}-${city.cityId}`}
            {...appDom({
              id: `spy-counter-guard-${spy.unitId}-${city.cityId}`,
              type: 'Button',
              role: 'button',
              scope: 'section',
            })}
            onClick={() => run(actions.postCounterSpy(spy.unitId, city.cityId))}
            className={BUTTON}
          >
            {t('espionage.counterGuard', { city: city.name })}
          </button>
        ))
      )}
      {counter.canSweep && (
        <button
          type="button"
          data-testid={`spy-counter-sweep-${spy.unitId}`}
          {...appDom({
            id: `spy-counter-sweep-${spy.unitId}`,
            type: 'Button',
            role: 'button',
            scope: 'section',
          })}
          onClick={() => run(actions.sweep(spy.unitId))}
          className={BUTTON}
        >
          {t('espionage.counterSweep', { gold: rules.sweepGold })}
        </button>
      )}
      {counter.interceptable.map((agent) => (
        <button
          key={agent.unitId}
          type="button"
          data-testid={`spy-counter-catch-${spy.unitId}-${agent.unitId}`}
          {...appDom({
            id: `spy-counter-catch-${spy.unitId}-${agent.unitId}`,
            type: 'Button',
            role: 'button',
            scope: 'section',
          })}
          onClick={() => run(actions.intercept(spy.unitId, agent.unitId))}
          className={BUTTON}
        >
          {t('espionage.counterCatch')}
        </button>
      ))}
      {refused && (
        <output className="text-[10px] text-destructive">{t('espionage.counterRefused')}</output>
      )}
    </div>
  );
}
